import config from "../../config";
import { uuid } from "../helpers";
import { EventSubscriber } from "./event-subscriber";
import IframeServiceStub from "./iframe-service-stub";

function isIframe(): boolean {
    return window.top !== window;
}

/** messages sent by rms client */
const RMS_CLIENT_TYPE = {
    READY: 'RMS_CLIENT_READY',
    REFRESH_REQUEST: 'REFRESH_REQUEST',
    REDIRECT_REQUEST: 'REDIRECT_REQUEST',
    RESIZE_IFRAME: 'RESIZE_IFRAME',
    OPEN_CHAT_REQUEST: 'OPEN_CHAT_REQUEST',
    NAVIGATE_RETURNS: 'NAVIGATE_RETURNS',
    CHANGE_PARENT_PATH: 'CHANGE_PARENT_PATH',
    CLOSE_ONBOARDING_WIZARD: 'CLOSE_ONBOARDING_WIZARD',
    GET_RMS_API_URL: 'GET_RMS_API_URL',
    UPDATE_RETURN_GUARD_LEFT_MENU_STATE: 'UPDATE_RETURN_GUARD_LEFT_MENU_STATE',
    GET_DASHBOARD_SETTINGS: 'GET_DASHBOARD_SETTINGS',
    LOGOUT: 'LOGOUT',
}

/** messages sent by laravel */
export const LARAVEL_TYPE = {
    SET_API_URL: 'LARAVEL_SET_API_URL',
    SET_DASHBOARD_SETTINGS: 'SET_DASHBOARD_SETTINGS',
    BROWSER_BACK_BUTTON_CLICKED: 'BROWSER_BACK_BUTTON_CLICKED',
    REQUEST_SHOP_SWITCH_REASON: 'REQUEST_SHOP_SWITCH_REASON',
} as const;

export interface IresizeIframeProps {
    iframeId: string, 
    height: number,
    width: number
}

export interface IChangeParentPath {
    path: string;
}

export interface INavigateReturnsProps {
    rmaid?: number; /** when set will navigate parent frame to url of a form http://app.returngo.ai/dashboard/returns?rmaid=<rma_id> */
}

export interface IIframeService {
    getApiUrl(): Promise<string|undefined>;
    getRmsApiUrl(): Promise<string|undefined>;
    getDashboardSettings(): Promise<string>;
    refreshParentFrame(): void;
    redirectParentFrame(url: string): Promise<boolean>;
    resizeIframe(props: IresizeIframeProps): Promise<boolean>;
    openParentIframeChat(): Promise<boolean>;
    navigateReturns(props: INavigateReturnsProps): Promise<boolean>;
    changeParentPath(path: string): Promise<void>;
    closeOnboardingWizardModal(): Promise<boolean>;
    updateLeftMenuReturnGuard(isSellingCoverage: boolean): Promise<boolean>;
    subscribeToParentEvent(eventName: keyof typeof LARAVEL_TYPE, listener: (message?: IEventMessage) => void): () => void;
    getInAppRedirectUrl(path: string, origin: string): string;
    logout(): Promise<void>;
}

interface IEventMessage {
    type: string;
    [payloadKey: string]: any;
}

class IframeService implements IIframeService {
    private _apiUrl?: string;
    private _subscriber = new EventSubscriber<IEventMessage>();

    constructor() {
        this._onMessageReceived = this._onMessageReceived.bind(this);
        this._sendMessage = this._sendMessage.bind(this);

        window.addEventListener('message', event => {
            const rawEventType = (event.data?.type ?? ''); 
            let eventType;
            if (typeof rawEventType === 'string') {
                eventType = rawEventType.split('_#')[0];
            }
            else {
                eventType = '';
                console.log('[IframeService] - rawEventType returns value of incorrect data type', event.data?.type);
            }

            /** listen to all known events */
            const allEventTypes = [ 
                ...Object.values(LARAVEL_TYPE), 
                ...Object.values(RMS_CLIENT_TYPE),
            ];
            if (eventType && allEventTypes.includes(eventType)) {
                this._onMessageReceived(event.data);
            }
        }, false);
    }

    private _sendMessage(message: IEventMessage) {
        window.top?.postMessage(message, '*');
    }

    private _onMessageReceived(message: IEventMessage) {
        this._subscriber.fire(message.type, message);
    }

    getApiUrl(): Promise<string> {
        return new Promise(resolve => {
            
            if (this._apiUrl) {
                return resolve(this._apiUrl);
            }

            const unsubscribe = this._subscriber.subscribe(LARAVEL_TYPE.SET_API_URL, message => {
                unsubscribe();
                console.log('[rms-client] setting apiUrl', message?.apiUrl);
                this._apiUrl = message?.apiUrl;
                resolve(message?.apiUrl);
            });

            this._sendMessage({ type: RMS_CLIENT_TYPE.READY });
            console.log('[rms-client] waiting for apiUrl');

        });        
    }

    getDashboardSettings(): Promise<string> {
        return new Promise<string>((resolve) => {
            const unsubscribe = this._subscriber.subscribe(LARAVEL_TYPE.SET_DASHBOARD_SETTINGS, message => {
                unsubscribe();
                const settings = message?.settings;
                resolve(settings);
            });

            this._sendMessage({ type: RMS_CLIENT_TYPE.GET_DASHBOARD_SETTINGS });
        });
    }

    refreshParentFrame(): void {
        this._sendMessage({ type: RMS_CLIENT_TYPE.REFRESH_REQUEST });
    }

    /** generic method to send request to parent frame and receive response */
    private _sendRequest<SendPayload, ReceivePayload>(event: string, payload: SendPayload): Promise<ReceivePayload> {
        return new Promise((resolve) => {
            const eventType = `${event}_#${uuid()}`;

            const unsubscribe = this._subscriber.subscribe(eventType, message => {
                console.log('[rms-client] received', message);
                unsubscribe();
                resolve(message?.payload as ReceivePayload);
            });

            const message = { 
                type: eventType,
                payload,
            };
            this._sendMessage(message);
            console.log('[rms-client] sent', message);
        });
    }

    async redirectParentFrame(url: string): Promise<boolean> {
        const res = await this._sendRequest<{ url: string }, { success: boolean }>(RMS_CLIENT_TYPE.REDIRECT_REQUEST, { url });
        return res.success;
    }

    async resizeIframe(props: IresizeIframeProps): Promise<boolean> {
        const res = await this._sendRequest<IresizeIframeProps, { success: boolean }>(
            RMS_CLIENT_TYPE.RESIZE_IFRAME, props
        );
        return res.success;
    }

    async openParentIframeChat(): Promise<boolean> {
        const res = await this._sendRequest<{}, { success: boolean }>(
            RMS_CLIENT_TYPE.OPEN_CHAT_REQUEST, {}
        );
        return res.success;
    }

    async navigateReturns(payload: INavigateReturnsProps): Promise<boolean> {
        const res = await this._sendRequest<INavigateReturnsProps, { success: boolean }>(
            RMS_CLIENT_TYPE.NAVIGATE_RETURNS, 
            payload
        );
        return res.success;
    }
    
    async changeParentPath(path: string): Promise<void> {
        await this._sendRequest<IChangeParentPath, { success: boolean }>(
            RMS_CLIENT_TYPE.CHANGE_PARENT_PATH,
            {path},
        );
    }

    async getRmsApiUrl(): Promise<string> {
        const res = await this._sendRequest<{}, { rmsApiUrl: string }>(
            RMS_CLIENT_TYPE.GET_RMS_API_URL, {}
        );
        return res.rmsApiUrl;
    }

    async closeOnboardingWizardModal(): Promise<boolean> {
        const res = await this._sendRequest<{}, { success: boolean }>(
            RMS_CLIENT_TYPE.CLOSE_ONBOARDING_WIZARD, {}
        );
        return res.success;
    }

    async updateLeftMenuReturnGuard(isSellingCoverage: boolean): Promise<boolean> {
        const res = await this._sendRequest<{}, { success: boolean }>(
            RMS_CLIENT_TYPE.UPDATE_RETURN_GUARD_LEFT_MENU_STATE, { isSellingCoverage }
        );
        return res.success;
    }

    public subscribeToParentEvent(eventName: keyof typeof LARAVEL_TYPE, listener: (message?: IEventMessage) => void) {
        return this._subscriber.subscribe(eventName, listener);
    }

    public getInAppRedirectUrl(path: string, origin: string): string {
        return `${config.apiUrl}/in-app-redirect/?path=${encodeURIComponent(path)}&origin=${encodeURIComponent(origin)}`;
    }

    public async logout(): Promise<void> {
        await this._sendRequest<{}, { success: boolean }>(
            RMS_CLIENT_TYPE.LOGOUT, {}
        );
    }
}

export default IframeService;


export function initIframeService(): IIframeService {
    if (isIframe()) {
        return new IframeService();
    }

    return new IframeServiceStub();
}




